import type { IconName } from "@brave/leo/icons/meta";
import type { PaymentProcessor } from "./payments-service";
import { updateLocaleSetting, type MatomoParams, type Subscription, type User } from "./subscription-service";
import { browser } from "$app/environment";
import { m } from "$lib/paraglide/messages";
import { getLocale, setLocale } from "./paraglide/runtime";

const userStorageKey = "user";

export type Mutate<T extends {}, K extends keyof T, J> = { [P in keyof T]: P extends K ? J : T[P] };

export const sleep = (seconds: number): Promise<void> => {
  return new Promise((resolve) => {
    setTimeout(resolve, seconds * 1000);
  });
};

export const getUser = (): User => {
  const user = sessionStorage.getItem(userStorageKey);
  return JSON.parse(user);
};

export const loadUser = (user: User): void => {
  if (!user) {
    throw new Error("Expected user to be object, but received " + user);
  }

  sessionStorage.setItem(userStorageKey, JSON.stringify(user));

  // if (!userSetting call Patch)
  // else set locale based on userSetting
  // TODO: this shouldn't go here
  // This should be moved to the custom `setLocale` function
  if (!user.subscriber_settings.locale) {
    updateLocaleSetting(user.subscriber_id, getLocale());
  }

  setLocale(user.subscriber_settings.locale ?? getLocale())
};

export const unloadUser = (): void => {
  sessionStorage.removeItem(userStorageKey);
};

// Note that if no user is found, then the trial must be considered available since
// we have no way of knowing otherwise.
export const isProductTrialAvailable = (productId: Subscription["product_id"]): boolean =>
  !getUser()?.used_trials?.includes(productId);

export const getFormattedDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  return Intl.DateTimeFormat(getLocale(), {
    month: "short",
    day: "2-digit",
    year: "numeric",
  }).format(date);
};

export const getFormattedDiscount = (discount: number) => `${discount * 100}%`;

export const getFormattedPrice = (price: number | string, currency: string = "USD"): string => {
    // Note that we are not using the `getLocale()` from the language picker.
    // This is so that we can leverage the region info which is likely to be
    // on the language set via the browser. If the user simply has `en`, then
    // we want to default to a format, like `en-AU`, that will be explicit
    // about the currency. E.g. USD 7.00
    const locale = navigator.language === "en" ? "en-AU" : navigator.languages;
    const formattedPrice = Intl.NumberFormat(locale, {
      style: "currency",
      currency,
    }).format(price as number);

    return formattedPrice;
  }

export const getRecoveryURL = (domain: string, orderId: string): string => {
  const payload = {
    recovery_expiration: new Date().getTime() + 60_000,
    order_id: orderId,
  };
  const orderPayload = btoa(JSON.stringify(payload));
  return `https://${domain}/?intent=recover&order=${orderPayload}`;
};

// @ts-ignore
export const hasBrowserSkusService = () => !!(navigator.brave && window.chrome?.braveSkus);

export const isLinux = (): boolean => !/Android/.test(navigator.userAgent) && /Linux|Ubuntu/.test(navigator.userAgent);

export const propagateSafeParams = (
  from: string | URL,
  to: string | URL,
  { allowAll }: { allowAll: boolean } = { allowAll: false }
): URL => {
  const safeParams = ["ux", "mtm_campaign", "mtm_source", "mtm_medium", "mtm_content"];

  let newTo: URL;
  try {
    from = new URL(from);

    /**
     * While this check could be placed above, it's being placed here
     * in order to ensure a URL object is returned.
     */
    if (!to) return from;

    newTo = new URL(to, from?.origin || location.origin);

    for (const [k, v] of from.searchParams) {
      if (!newTo.searchParams.has(k) && (allowAll || safeParams.includes(k))) {
        newTo.searchParams.append(k, v);
      }
    }
  } catch (e) {
    console.error(e);
  }

  return newTo;
};

// @ts-ignore
export const isBrave = (): boolean => navigator.brave?.isBrave;

// intentionally in global scope for use by referral.js
export const is_iOS = () => {
  /**
   * Note that navigator.platform has been deprecated, so the below
   * will have to be refactored to use navigator.userAgentData.platform
   * when there is broader support.
   * See https://developer.mozilla.org/en-US/docs/Web/API/Navigator/platform
   */
  // prettier-ignore
  // @ts-ignore
  return (/iPad|iPhone|iPod/.test(navigator.platform) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)) && !window.MSStream;
};

export const isAndroid = () => {
  return /Android/.test(navigator.userAgent);
};

export const isMobile = () => {
  return isAndroid() || is_iOS();
};

export const isMinChromiumVersion = (minVersion: number): boolean => {
  let { cv } = navigator.userAgent.match(/Chrome\/(?<cv>\d{3})/)?.groups ?? {};
  const chromiumVersion = Number(cv);

  return chromiumVersion >= minVersion;
};

export const paymentProcessorDisplay: Record<PaymentProcessor, { icon: IconName; text: string }> = {
  stripe: {
    icon: "payment-stripe-color",
    text: m.payment_stripe_text(),
  },
  ios: {
    icon: "payment-apple-color",
    text: m.payment_ios_text(),
  },
  android: {
    icon: "payment-googleplay-color",
    text: m.payment_android_text(),
  },
};

export const checkBrowserSupport = (predicateFn?: () => string | undefined) => {
  if (!browser) return;

  let isSupported = true;
  let message: string;

  // If the predicate function returns a truthy value, then the browser isn't supported
  let predicateResult = predicateFn?.();
  if (predicateResult) {
    isSupported = false;
    message = predicateResult;
  }

  return {
    message: message as string,
    isSupported,
  };
};

export const getMatomoParams = (): MatomoParams => {
  const searchParams = new URLSearchParams(location.search);

  const mtm_campaign = searchParams.get("mtm_campaign");
  const mtm_source = searchParams.get("mtm_source");
  const mtm_medium = searchParams.get("mtm_medium");
  const mtm_content = searchParams.get("mtm_content");

  if (!mtm_campaign && !mtm_source) {
    return;
  }

  return {
    mtm_campaign,
    mtm_source,
    mtm_medium,
    mtm_content,
  }
}
